// Estilos da lista de atendimentos (M5.1) da modernização do InfoChat.
//
// Função pura — sem JSX, sem I/O, sem leitura de localStorage/backend.
// Recebe o `theme` do MUI (idealmente já com `theme.infoChat` da Entrega
// M2) e devolve só objetos de estilo simples (CSS-in-JS), para serem
// mesclados nas classes já existentes de `TicketsManagerTabs` e
// `TicketListItemCustom`. Não aplica nada sozinha — quem decide onde/como
// usar é cada um desses dois arquivos.
//
// Escopo desta entrega (M5.1): só a apresentação da lista — busca, filtros,
// abas, contadores, badges e a aparência (cor) dos botões de ação já
// existentes nas linhas de ticket. Nenhuma rota, condição, handler,
// endpoint, payload ou estrutura JSX é decidida aqui.
//
// Usa exclusivamente os tokens de `theme.infoChat` para cor — nunca um hex
// literal novo aqui. Exceções documentadas:
//  - `"#fff"` como cor de texto sobre um fundo na cor institucional
//    (contraste fixo, mesmo padrão já usado em `infoChatNavigationStyles.js`
//    da M4 — não tem token equivalente no sistema da M2).
//  - As cores oficiais de WhatsApp/Facebook/Instagram e a cor própria de
//    cada fila (`queue.color`, vinda do backend) NÃO fazem parte deste
//    helper — permanecem exatamente como estão nos dois arquivos
//    consumidores, por decisão explícita (identidade de marca/dado do
//    usuário, não token de design).

import { createInfoChatDesignTokens } from "../theme/infoChatDesignTokens";

const hasCompleteInfoChatTokens = tokens =>
  Boolean(
    tokens &&
      tokens.brand &&
      tokens.surfaces &&
      tokens.borders &&
      tokens.text &&
      tokens.shadows &&
      tokens.transitions &&
      tokens.radii &&
      tokens.spacing &&
      tokens.status
  );

// Mesmo padrão de fallback seguro já usado em infoChatNavigationStyles.js
// (M4), infoChatGlobalShellStyles.js (M3) e infoChatLoginStyles.js
// (LOGIN-2) — nunca deixa a lista quebrar caso theme.infoChat esteja
// ausente/incompleto, e nunca diverge dos tokens reais por reaproveitar a
// mesma função pura da M2.
const resolveInfoChatTokens = theme => {
  if (hasCompleteInfoChatTokens(theme?.infoChat)) {
    return theme.infoChat;
  }
  return createInfoChatDesignTokens(theme?.palette?.type, theme?.palette?.primary?.main);
};

/**
 * Estilos da lista de atendimentos modernizada (M5.1).
 *
 * @param {object} theme - tema do MUI (idealmente já com `theme.infoChat`).
 */
export const createInfoChatTicketsListStyles = theme => {
  const tokens = resolveInfoChatTokens(theme);

  return {
    // ---- TicketsManagerTabs (busca, filtros, abas, contadores) ----

    // Campo de busca.
    searchSurface: {
      backgroundColor: tokens.mode === "dark" ? tokens.surfaces.raised : tokens.surfaces.base,
      borderColor: tokens.borders.subtle
    },
    searchIconColor: {
      color: tokens.text.secondary
    },

    // Caixa de ações rápidas (Todos/Novo/Resolver todos/Abertos/Resolvidos/Ordenar).
    optionsSurface: {
      backgroundColor: tokens.mode === "dark" ? tokens.surfaces.raised : tokens.surfaces.subtle,
      borderColor: tokens.borders.subtle
    },
    actionButtonActiveBorder: {
      borderColor: tokens.brand.primaryColor
    },
    actionIconColor: {
      color: tokens.text.secondary
    },
    actionIconActiveColor: {
      color: tokens.brand.primaryColor
    },

    // Ícone do filtro.
    filterIconColor: {
      color: tokens.brand.primaryColor
    },

    // Abas de status (Atendendo/Aguardando/Grupos).
    tabsSurface: {
      backgroundColor: tokens.mode === "dark" ? tokens.surfaces.raised : tokens.surfaces.subtle
    },
    tabIndicatorColor: {
      backgroundColor: tokens.brand.primaryColor
    },
    tabItemSelected: {
      backgroundColor: `${tokens.brand.primaryColor}0D`,
      boxShadow: tokens.shadows.sm
    },
    tabItemHover: {
      backgroundColor: `${tokens.brand.primaryColor}08`
    },
    // Cor do ícone/texto da aba ativa (usada inline nos labels das Tabs).
    activeTabColor: tokens.brand.primaryColor,

    // Badges de contagem (nas abas) e de ação (tooltip "Todos"/"Novo"/etc.)
    // — mesmo par fundo-institucional + texto branco fixo já usado para
    // ícones sobre fundo colorido na M4 (sem token de contraste equivalente
    // no sistema da M2).
    countBadge: {
      backgroundColor: tokens.brand.primaryColor,
      color: "#fff"
    },
    actionTooltipBadge: {
      backgroundColor: tokens.brand.primaryColor,
      color: "#fff"
    },

    // ---- TicketListItemCustom (cartão/linha do ticket) ----

    listItemPrimaryText: {
      color: tokens.text.primary
    },
    listItemSecondaryText: {
      color: tokens.text.secondary
    },

    // Destaque de "não lido" (horário, contador, badge) — reaproveita o
    // token semântico "healthy" (positivo/ativo) da M2 em vez do "green"
    // literal usado antes.
    unreadAccentColor: {
      color: tokens.status.healthy.main
    },
    unreadBadge: {
      backgroundColor: tokens.status.healthy.main,
      color: "#fff"
    },

    // Indicador de tempo de espera (ticket pendente) — reaproveita o token
    // semântico "awaitingScan" (aguardando) da M2 em vez do
    // "#fff8e1"/"#f57c00" hardcoded usado antes.
    pendingIndicator: {
      backgroundColor: tokens.status.awaitingScan.surface,
      color: tokens.status.awaitingScan.main,
      borderColor: tokens.status.awaitingScan.main
    },

    // Fundo do badge de fila quando o ticket não tem fila própria/cor
    // definida (fallback — nunca substitui `queue.color`, que é dado do
    // usuário, não token de design).
    queueBadgeFallback: {
      backgroundColor: tokens.text.secondary
    },

    // Cor dos 5 botões de ação existentes na linha do ticket — mesma
    // estrutura/condições de exibição de hoje, só a cor passa a vir de um
    // token semântico em vez de "blue"/"green"/"purple"/"red" literais (que
    // hoje também viravam "#fff" plano no modo escuro, perdendo a
    // distinção entre ações). Cada token já contrasta bem nos dois modos,
    // então não há mais necessidade de ramificar por theme.mode aqui.
    actionButtonView: {
      color: tokens.status.connecting.main
    },
    actionButtonAccept: {
      color: tokens.status.healthy.main
    },
    actionButtonTransfer: {
      color: tokens.brand.primaryColor
    },
    actionButtonClose: {
      color: tokens.status.alert.main
    },
    actionButtonCreate: {
      color: tokens.status.healthy.main
    },

    // Superfície do ticket selecionado na lista — pill sutil na cor
    // institucional (mesmo raciocínio do item ativo do menu na M4), em vez
    // do cinza padrão do MUI. Só fundo: nunca recolore o texto, para não
    // arriscar legibilidade do status pendente/badges já existentes.
    selectedItemSurface: {
      backgroundColor: tokens.mode === "dark" ? tokens.surfaces.raised : `${tokens.brand.primaryColor}12`
    },
    // Mesmo ticket selecionado, mas também sob hover — um pouco mais forte
    // que o estado apenas selecionado, para o hover continuar perceptível.
    selectedItemHoverSurface: {
      backgroundColor: `${tokens.brand.primaryColor}${tokens.mode === "dark" ? "18" : "1F"}`
    },

    // Anel de foco visível (navegação real por teclado) — mesma cor
    // institucional. Usa `outline`/`outlineOffset`, nunca `boxShadow`:
    // mesmo raciocínio já corrigido em `infoChatNavigationStyles.js` (M4) —
    // o ticket selecionado usa `backgroundColor` (acima) para o destaque, e
    // as abas/botões usam `backgroundColor`/transform no hover, então um
    // anel via `boxShadow` disputaria a mesma propriedade em vez de
    // coexistir. `outline` é uma propriedade CSS totalmente independente
    // (nunca afeta layout, mesmo com offset negativo), então o foco sempre
    // aparece por cima de qualquer combinação de estado (selecionado,
    // hover, aba ativa) sem sobrescrever nada.
    focusRingAccent: {
      outline: `2px solid ${tokens.brand.primaryColor}`,
      outlineOffset: "-2px"
    }
  };
};

export default createInfoChatTicketsListStyles;
